/**
 * Accepted capture zone.
 *
 * The app ONLY records coordinates inside this box — the UCSI KL campus and
 * its immediate Cheras / KL surroundings. Anything outside is rejected so the
 * notebook can never silently carry a bogus "block" coordinate that a check-in
 * would later submit.
 *
 * Tune these if a new campus / satellite campus needs to be captured.
 */

export type Bounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

/** Known-good campus coordinate (verified via a real successful check-in). */
export const CAMPUS = {
  latitude: 3.0789873242342494,
  longitude: 101.73321165223452,
} as const;

export const ACCEPTED_BOUNDS: Bounds = {
  minLat: 3.05,
  maxLat: 3.11,
  minLon: 101.71,
  maxLon: 101.76,
};

/** GPS fixes at or below this accuracy are considered "good enough" to capture. */
export const GPS_OK_ACCURACY_M = 20;
/** Above this accuracy a fix is flagged as poor and capture is blocked. */
export const GPS_BAD_ACCURACY_M = 30;

/** Approx metres per degree of latitude (for rough distance hints only). */
const M_PER_DEG_LAT = 111_320;

function degLonWidthM(lat: number): number {
  return M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
}

export function isWithinBounds(latitude: number, longitude: number): boolean {
  const b = ACCEPTED_BOUNDS;
  return (
    latitude >= b.minLat &&
    latitude <= b.maxLat &&
    longitude >= b.minLon &&
    longitude <= b.maxLon
  );
}

/** Rough distance (m) from a point to the edge of the accepted zone. */
export function distanceToZone(latitude: number, longitude: number): number {
  const b = ACCEPTED_BOUNDS;
  const dLat = Math.max(b.minLat - latitude, latitude - b.maxLat, 0);
  const dLon = Math.max(b.minLon - longitude, longitude - b.maxLon, 0);
  return Math.hypot(dLat * M_PER_DEG_LAT, dLon * degLonWidthM(latitude));
}

/** Human hint like "move 120 m north-east to enter the capture zone". */
export function outOfBoundsHint(latitude: number, longitude: number): string {
  const b = ACCEPTED_BOUNDS;
  const dirs: string[] = [];
  let dx = 0;
  let dy = 0;
  if (latitude < b.minLat) {
    dirs.push("north");
    dx = b.minLat - latitude;
  } else if (latitude > b.maxLat) {
    dirs.push("south");
    dx = latitude - b.maxLat;
  }
  if (longitude < b.minLon) {
    dirs.push("east");
    dy = b.minLon - longitude;
  } else if (longitude > b.maxLon) {
    dirs.push("west");
    dy = longitude - b.maxLon;
  }
  const dist = Math.hypot(
    dx * M_PER_DEG_LAT,
    dy * degLonWidthM(latitude),
  );
  const m = Math.round(dist);
  return `move ${m} m ${dirs.join("-")} to enter the capture zone`;
}